import React from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Lock } from "lucide-react";
import { StatusBadge } from "../../shared/StatusBadge";
import { Button } from "../../ui/button";
import { routeStatusMeta } from "../../../lib/statusMaps";
import { formatDistanceKm } from "../../../lib/utils";

export function SavedRoutesPanel({ routes, loading, onEdit, onDelete, editingRouteIds, routeColorMap }) {
  return (
    <div className="rounded-md border border-border bg-surface p-4" data-testid="saved-routes-panel">
      <p className="mb-2 font-heading text-sm font-semibold text-text-main">Saved Routes for This Date</p>
      {loading && <p className="text-xs text-text-muted">Loading...</p>}
      {!loading && routes.length === 0 && <p className="text-xs text-text-muted">No routes finalized yet for this date.</p>}
      <div className="space-y-1.5">
        {routes.map((r) => {
          const m = routeStatusMeta(r.status);
          const locked = (r.deliveries_done || 0) > 0 || r.packing_started;
          const lockReason = r.packing_started ? "Packing started" : "Locked";
          const isEditing = editingRouteIds?.has(r.id);
          const color = routeColorMap?.get(r.id);
          return (
            <div
              key={r.id}
              className="flex items-center justify-between gap-2 rounded-sm border border-border px-3 py-2 text-xs"
              data-testid={`saved-route-row-${r.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {color && <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />}
                <Link to={`/routes/${r.id}`} className="truncate font-medium text-text-main hover:underline" data-testid={`saved-route-view-link-${r.id}`}>
                  #{r.id} — {r.driver_name || "Unassigned"}
                </Link>
              </div>
              <div className="flex shrink-0 items-center gap-2 text-text-muted">
                <span>{r.deliveries_done}/{r.deliveries_total} · {formatDistanceKm(r.distance)}</span>
                <StatusBadge label={m.label} variant={m.variant} />
                {locked ? (
                  <span className="flex items-center gap-1 text-text-muted" title={r.packing_started ? "Packing has started for this route" : undefined} data-testid={`saved-route-locked-${r.id}`}>
                    <Lock size={12} strokeWidth={1.5} /> {lockReason}
                  </span>
                ) : isEditing ? (
                  <span className="font-medium text-accent" data-testid={`saved-route-editing-badge-${r.id}`}>Editing…</span>
                ) : (
                  <>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onEdit(r)} data-testid={`saved-route-edit-button-${r.id}`}>
                      <Pencil size={13} strokeWidth={1.5} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-danger" onClick={() => onDelete(r)} data-testid={`saved-route-delete-button-${r.id}`}>
                      <Trash2 size={13} strokeWidth={1.5} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
